import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {AccessToken, loginUser, signupUser, User} from "../../app/interfaces";
import {Storage} from "@ionic/storage";
import 'rxjs/Rx';
import {environment} from "../../environment";
import {catchError, mergeMap, mergeMapTo, tap} from "rxjs/operators";
import {BehaviorSubject} from "rxjs";
import {AlertController, App} from "ionic-angular";
import {TranslateService} from "@ngx-translate/core";
import {FileTransfer, FileUploadOptions} from "@ionic-native/file-transfer";
import {fromPromise} from "rxjs/observable/fromPromise";
import {UniqueDeviceID} from "@ionic-native/unique-device-id";
import {of} from "rxjs/observable/of";

const KEY_USER_TOKEN = 'token';

const KEY_LOGGED_USER = 'loggedUser';

const KEY_IGNORAR_PASSWORD_INSEGURO = 'ignorar_password_inseguro';

@Injectable()
export class LoginProvider {

  user: BehaviorSubject<User> = new BehaviorSubject(null);

  get currentUser() {
    return this.user.getValue();
  }

  servidor: string;

  private _userToken: string;

  get userToken(): string {
    return this._userToken;
  }

  set userToken(value: string) {
    this.storage.set(KEY_USER_TOKEN, value);
    this._userToken = value;
  }

  constructor(public http: HttpClient,
              private storage: Storage,
              private app: App,
              private fileTransfer: FileTransfer,
              private alertController: AlertController,
              private uniqueDeviceId: UniqueDeviceID,
              private translateService: TranslateService) {
    this.servidor = environment.serverAddress;
  }

  async loadTokenFromStorage() {
    const token = await this.storage.get(KEY_USER_TOKEN);
    this._userToken = token;
    if (token !== null) {
      this.tokenIsValid().toPromise();
    }
    return token !== null;
  }


  createUser(user: signupUser) {
    let url = this.servidor + "/api/register";
    let headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });
    let body = JSON.stringify(user);

    return this.http.post(url, body, {headers})
  }


  restorePassword(updatePasswordFormValues, token?) {
    let url = environment.serverAddress + "/api/user/password";

    return this.http.put(url, updatePasswordFormValues).pipe(mergeMapTo(this.tokenIsValid()));
  }

  loginUser(user: loginUser) {
    let url = this.servidor + "/api/auth/login";

    return this.conseguirUUID().pipe(
      mergeMap((uuid) => {
        let body = JSON.stringify({
          uuid: uuid,
          ...user
        });

        return this.http.post<AccessToken>(url, body).pipe(tap((result: AccessToken) => {
          this.userToken = result.access_token;
          this.tokenIsValid().toPromise();
        }));
      })
    );
  }

  requestDeviceUnlock(user: loginUser) {
    let url = this.servidor + "/api/auth/request-unlock";
    let body = JSON.stringify(user);

    return this.http.post(url, body);
  }

  loginWithFacebook(userToken: string) {
    let url = this.servidor + "/api/auth/facebook"
    let body = {
      user_token: userToken
    };
    return this.http.post(url, body);
  }

  editaDataUser(userDataFormValues: any, token?: string) {
    let url = environment.serverAddress + "/api/user";

    return this.http.put(url, userDataFormValues).pipe(mergeMapTo(this.tokenIsValid()));
  }

  updateUserImage(imagePath: string) {
    let fileTrans = this.fileTransfer.create();
    let url = environment.serverAddress + "/api/user/photo";

    let headers = {
      'Authorization': "Bearer " + this._userToken,
      'Accept': 'application/json'
    };

    let fileUploadOptions: FileUploadOptions = {
      httpMethod: "POST",
      fileKey: "image",
      headers: headers
    };

    return fromPromise(fileTrans.upload(imagePath, url, fileUploadOptions)).pipe(mergeMapTo(this.tokenIsValid()));
  }

  tokenIsValid(token?: String) {
    let url = this.servidor + "/api/user";


    return this.conseguirUUID().pipe(
      mergeMap((uuid) => {

        return this.http.get(url,
          {
            params: {
              uuid: uuid,
            }
          }
        ).pipe(tap((user: User) => {
          this.storage.set(KEY_LOGGED_USER, user);
          this.user.next(user);
          if (user.vulnerable) {
            this.mostrarAlertaSobrePasswordInseguro();
          }
        })).pipe(catchError(async () => {
          const userFromStorage = await this.storage.get(KEY_LOGGED_USER);
          if (userFromStorage !== null) {
            this.user.next(userFromStorage);
          }
        }));
      })
    );
  }

  logOut(token?: String) {
    let url = this.servidor + "/api/auth/logout";
    const userToken = this._userToken;
    this._userToken = null;
    this.storage.clear();
    return this.http.post(url, null, {
      headers: {
        'Authorization': `Bearer ${userToken}`
      }
    });
  }

  recoverPassword(email: any) {
    let url = this.servidor + "/api/auth/reset";
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    });
    return this.http.post(url, email, {headers})
  }

  conseguirUUID() {
    return fromPromise(this.uniqueDeviceId.get()).pipe(
      catchError((err) => {
        return of("ionic-serve")
      })
    );
    // return of("ionic-serve");
  }

  async mostrarAlertaSobrePasswordInseguro() {
    const usuarioPrefiereIgnorar = await this.storage.get(KEY_IGNORAR_PASSWORD_INSEGURO).catch(err => false);
    if (usuarioPrefiereIgnorar) {
      return;
    }
    const traducciones = this.translateService.instant([
      'your_password_is_insecure',
      'we_suggest_you_to_change_your_password_as_soon_as_possible_to_make_your_account_more_secure',
      'take_me_there',
      'remind_me_later',
    ]);
    this.alertController.create({
      title: traducciones['your_password_is_insecure'],
      message: traducciones['we_suggest_you_to_change_your_password_as_soon_as_possible_to_make_your_account_more_secure'],
      enableBackdropDismiss: false,
      buttons: [
        {
          text: traducciones['take_me_there'],
          handler: () => {
            this.app.getRootNav().push('EditarDatosUsuarioPage');
          }
        },
        {
          text: traducciones['remind_me_later'],
          role: 'cancel'
        },
      ]
    }).present();
  }
}



// WEBPACK FOOTER //
// ./src/providers/login/login.ts